// useStudioFind — the canvas's "Find on this desk" (studioFind.ts,
// StudioFind.tsx): whether it is open, its text and filter (view-ephemeral:
// none of it survives a reload), what matches, and how each card shows.

export type { FindLook };
export { useStudioFind };

import { useState } from "react";
import type { StudioCard } from "./studioCards.js";
import {
    type FindFilter,
    type FindItem,
    findItems,
    findMatches,
    litCards,
} from "./studioFind.js";

// How a card shows while something is looked for: lit (with how many
// matches lie inside it), or faded. Undefined: nothing is looked for.
type FindLook =
    | { readonly type: "lit"; readonly inside: number }
    | { readonly type: "faded" };

type StudioFindState = {
    readonly open: boolean;
    readonly text: string;
    readonly filter: FindFilter;
    readonly hits: readonly FindItem[];
    readonly lookOf: (id: string) => FindLook | undefined;
    readonly setOpen: (open: boolean) => void;
    readonly setText: (text: string) => void;
    readonly setFilter: (filter: FindFilter) => void;
};

const useStudioFind = (cards: readonly StudioCard[]): StudioFindState => {
    const [open, setOpenState] = useState(false);
    const [text, setText] = useState("");
    const [filter, setFilter] = useState<FindFilter>("all");
    const looking = open && (text.trim() !== "" || filter !== "all");
    const hits = looking ? findMatches(findItems(cards), text, filter) : [];
    const lit = litCards(hits);
    return {
        open,
        text,
        filter,
        hits,
        lookOf: (id) => {
            if (!looking) return undefined;
            const inside = lit.get(id);
            return inside === undefined
                ? { type: "faded" }
                : { type: "lit", inside };
        },
        // Closing forgets what was looked for.
        setOpen: (next) => {
            setOpenState(next);
            if (!next) {
                setText("");
                setFilter("all");
            }
        },
        setText,
        setFilter,
    };
};
